<template>
	<app-title-bar
		p-title-bar
		:icon="['fas', 'fa-truck']"
		subtitle="Yard App"
		title="Load Items List"
		:badges="[contract?.contract_number, contract?.account_name]">
		<app-button to="/yard-app/load-items" icon="fa-circle-left"> Back </app-button>
	</app-title-bar>

	<app-panel title="Items">
		<template #header>
			<div class="flex gap-2">
				<app-badge>{{ contract?.contract_number }}</app-badge>
				<app-badge>{{ contract?.account }}</app-badge>
			</div>
		</template>

		<load-detail-table
			:items="items"
			:loading="loading"
			@updated-items="updated"
			@updated-note="(item) => updatedNotes(item)" />

		<div class="flex justify-between mt-5 gap-3">
			<app-button icon="fa-save" class="ml-auto" @click="send(true)">Save</app-button>
			<app-button icon="fa-computer" @click="sendInterceptor">Send to Hire Desk</app-button>
		</div>
	</app-panel>
	<confirm-dialog
		:open="qtyDiscrepancyWarning"
		@confirmed="send"
		@canceled="qtyDiscrepancyWarning = false">
		Warning: Quantity discrepancies exist. Are you sure you want to Send to Hiredesk?
	</confirm-dialog>
</template>

<script lang="ts" setup>
	import { ref, onMounted } from 'vue'
	import LoadDetailTable from '@/components/datatables/yard-app/load/LoadDetailTable.vue'
	import useContracts from '@/composables/contracts'
	import { formatItemsDates } from '@/utils/date'
	import { getAllItems } from '@/utils/contract-items'
	import { useRoute } from 'vue-router'
	import useToasterStore from '@/stores/toaster'
	import { ContractItem } from '@/data/schema/contract-item'
	import ContractService from '@/services/contract'
	import type { StatusUpdate } from '@/data/interfaces/status-update'
	import { Contract } from '@/data/schema/contract'
	import useAuthStore from '@/stores/auth'
	import { statusParam } from '@/composables/contracts'
	import { ContractItemStatus } from '@/data/schema/contract-item-status'

	// Resources
	const api = new ContractService()
	const route = useRoute()
	const toaster = useToasterStore()
	const authStore = useAuthStore()

	// Data
	const contractNumber = ref()
	const contractNote = ref<string | null>('')
	const contract = ref<Contract>()
	const items = ref<ContractItem[]>([])
	const loading = ref(true)
	const qtyDiscrepancyWarning = ref(false)

	/**
	 * Get contract and contract item data
	 */
	async function getData() {
		loading.value = true

		const { fetchData } = useContracts()

		try {
			const data = await fetchData(contractNumber.value, {
				withItems: false,
				withPickItems: true,
				withContractStatus: true,
				withPickStatus: true,
				loadOperatorId: authStore.user?.id
			})
			contract.value = data.contract
			items.value = formatItemsDates(data.items)

			if (
				contract.value.status &&
				contract.value.status[0] &&
				contract.value.status[0].notes
			) {
				contractNote.value = contract.value.status[0].notes ?? null
			}

			loading.value = false
		} catch (error) {
			console.error(error)
			loading.value = false
		}
	}

	function sendInterceptor() {
		let quantitiesMatch = true

		quantitiesMatch = getAllItems(items.value).every((item) => {
			return (
				statusParam(item, 'quantity_loaded', 'pick') ===
				statusParam(item, 'quantity_confirmed', 'pick')
			)
		})

		quantitiesMatch ? send() : (qtyDiscrepancyWarning.value = true)
	}

	/**
	 * Save contract items quantity values
	 */
	function send(save = false) {
		qtyDiscrepancyWarning.value = false

		const saveItems = getAllItems(items.value).map((item) => ({
			id: item.id,
			status_id: item.pick_status?.[0].id,
			quantity_load: item.pick_status?.[0].quantity_loaded
		}))

		if (save) {
			toaster.working({
				text: 'Saving changes...',
				dismissable: false,
				timeout: 0
			})
		} else {
			toaster.working({
				text: 'Sending to hire desk...',
				dismissable: false,
				timeout: 0
			})
		}

		api.bulkUpdateStatus(saveItems as StatusUpdate[], 'pick-status', save)
			.then(() => {
				toaster.clear()
				toaster.success({ text: 'Contract saved successfully!' })
				getData()
			})
			.catch((error: any) => {
				toaster.clear()
				toaster.error({ text: 'Error occurred updating contract items' })
				console.error(error)
			})
			.catch((error: any) => {
				toaster.clear()
				toaster.error({ text: 'Error occurred updating contract' })
				console.error(error)
			})
	}

	/**
	 * Items have been updated
	 */
	function updated(item: ContractItem) {
		const index = items.value.findIndex((i) => i.id === item.id)

		if (index !== -1) {
			items.value[index] = item
			items.value[index].changed = true
		}
	}

	function updatedNotes(item: ContractItem) {
		const updatePickStatusNotes = (pickStatus: ContractItemStatus[], notes: string) => {
			if (pickStatus.length > 0) {
				pickStatus[0].notes = notes
				pickStatus[0].notes_status = 4
			}
		}
		const itemIndex = items.value.findIndex((i) =>
			i.id === item.component_parent ? item.component_parent : item.id
		)
		const componentIndex = items.value[itemIndex].kit_components?.findIndex(
			(i) => i.id === item.id
		)

		if (itemIndex === -1) {
			return
		}

		if (!componentIndex || componentIndex === -1) {
			updatePickStatusNotes(items.value[itemIndex].pick_status!, item.pick_status![0].notes!)
		} else {
			updatePickStatusNotes(
				items.value[itemIndex].kit_components![componentIndex!].pick_status!,
				item.pick_status![0].notes!
			)
		}
	}

	onMounted(() => {
		contractNumber.value = route.params.id

		if (contractNumber.value) {
			getData()
		}
	})
</script>
